<svelte:options runes={true} />

<script module>
  export const CARD_SELECTOR = ".da-product-card-grid .da-card";
</script>

<script lang="ts">
  import EmptyState from "~/components/ui/EmptyState/EmptyState.svelte";
  import ProductCard from "~/components/product/ProductCard/ProductCard.svelte";
  import type { ProductCardProps } from "~/components/product/ProductCard/types";
  import { useWtoVariant } from "~/lib/wto-experiments.svelte";

  interface Props {
    products: ProductCardProps[];
    loading?: boolean;
    skeletonCount?: number;
    class?: string;
  }

  let { products, loading = false, skeletonCount = 12, class: className = "" }: Props = $props();

  // Mounting all 48 cards' real data in one synchronous pass blocks the LCP
  // card's own image from being requested until every card below it has also
  // resolved. Give real data to just the first row synchronously; the rest
  // keep rendering as skeleton cards (same pending-promise mechanism as the
  // full-grid loading state below) for two rAFs, then get their real data.
  // The grid's element count never changes — only each card's content does —
  // so there's no collapse-then-regrow. Graduated from a ?stagedReveal=1-gated
  // production A/B (2026-07-21, see aurora-plp-lcp-bottleneck-recap-2026-07-21.md):
  // 13 runs per arm showed a real, modest LCP win (~232ms median, ~74% pairwise
  // win rate) with a clean paint-phase reduction and no run categorically worse.
  //
  // FIX (same day): the first version replaced the whole card list with only
  // the first 4 real ones, dropping the other 44 from the DOM entirely. On
  // production, the loading skeleton renders `skeletonCount` cards (derived
  // from the real row count, e.g. 48 — not this component's own 12 default),
  // so the grid was collapsing from 48 skeleton cards down to 4 real ones
  // before growing back — a real CLS risk that Storybook/production A/B
  // testing never exercised, since neither drove the true loading=true→false
  // transition with a realistic skeletonCount. Now every product keeps its
  // grid slot; only its `data` prop switches from a pending promise (skeleton)
  // to the resolved value, in place.
  const FIRST_BATCH_SIZE = 4;

  let revealedCount = $state(0);
  // plain (non-reactive) on purpose: reading+writing this as $state inside the
  // same $effect below created a self-trigger that re-ran the effect a second
  // time synchronously, immediately overwriting revealedCount before any rAF
  // fired — collapsing the staged reveal into a no-op. Confirmed via Storybook.
  let wasLoading = true;

  $effect(() => {
    const justFinishedLoading = wasLoading && !loading;
    wasLoading = loading;

    if (loading) return;

    if (justFinishedLoading && products.length > FIRST_BATCH_SIZE) {
      revealedCount = FIRST_BATCH_SIZE;
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          revealedCount = products.length;
        });
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
    revealedCount = products.length;
  });

  // WTO dev test: ta_DevTest-PLPDiscountBadgingLocation — Beige/Red move the promo
  // "Save" badge from the image overlay into the inline Price badge. Experiment IDs
  // from the WTO dashboard (only assigned once the test is saved there).
  const PLP_BADGE_LOCATION_TEST = "ta_DevTest-PLPDiscountBadgingLocation";
  const PLP_BADGE_LOCATION_BEIGE = "40992";
  const PLP_BADGE_LOCATION_RED = "40993";

  const plpBadgeLocationVariant = useWtoVariant(PLP_BADGE_LOCATION_TEST);
  const showSaveBadge = $derived(
    plpBadgeLocationVariant.value === PLP_BADGE_LOCATION_BEIGE ||
      plpBadgeLocationVariant.value === PLP_BADGE_LOCATION_RED,
  );
  const saveBadgeColor = $derived(
    plpBadgeLocationVariant.value === PLP_BADGE_LOCATION_RED ? "red" : "primary",
  );
</script>

{#if !loading && products.length === 0}
  <EmptyState>No products</EmptyState>
{:else}
  <div class={["da-product-card-grid", className]}>
    {#if loading}
      {#each { length: skeletonCount }, i (i)}
        <ProductCard data={new Promise(() => {})} />
      {/each}
    {:else}
      {#each products as product, i (product.productId ?? i)}
        <ProductCard
          data={i < revealedCount ? product : new Promise(() => {})}
          index={i}
          {showSaveBadge}
          {saveBadgeColor}
          hideDiscountBadge={showSaveBadge}
        />
      {/each}
    {/if}
  </div>
{/if}

<style>
  @layer aurora {
    .da-product-card-grid {
      container-type: inline-size;
      display: grid;
      width: 100%;
      padding: var(--da-space-4);
      row-gap: var(--da-space-3);
      column-gap: var(--da-space-3);
      max-width: 192rem;
      margin: 0 auto;
    }

    @container (width >= 330px) {
      .da-product-card-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        row-gap: var(--da-space-5);
        column-gap: var(--da-space-4);
      }
    }

    @container (width >= 600px) {
      .da-product-card-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    }

    @container (width >= 900px) {
      .da-product-card-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
    }

    @container (width >= 1200px) {
      .da-product-card-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
        column-gap: var(--da-space-6);
      }
    }
  }
</style>
